import { isLoading } from "apollo/utils";
import clsx from "clsx";
import {
  FILE_SCOPE,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { getStaticAsset } from "core/model/config";
import { formatUnixDate } from "core/model/utils/dates";
import { activateDTAAttachments } from "core/model/utils/featureFlags";
import {
  Auction,
  FileParams,
  FileState,
  Patient,
  QueryProgress,
} from "core/types";
import { Button, Card, CardBody, Image, Spinner } from "ds/ui";
import { PdfViewer, PdfViewerFile } from "dsl/atoms/PDFViewer";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useCreateFileUrl } from "dsl/ecosystems/PatientFiles/useDownloadFile";
import { useGetFilesFeatures } from "dsl/ecosystems/PatientFiles/useGetFilesFeatures";
import { getFileNameAndIsNewVersionAvailable } from "dsl/ecosystems/PatientFiles/utils";
import { ArrowLeftIcon, FileTextIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "translations";

const SourceContent = ({
  externalId,
  files,
  isLoadingFiles,
}: {
  files: { name: string; file: FileState }[];
  externalId: string;
  isLoadingFiles: boolean;
}) => {
  const translations = useTranslations();
  const locale = useLocale();

  const [createFileUrlProgress, setCreateFileUrlProgress] =
    useState<QueryProgress>(QUERY_PROGRESS_NOT_STARTED);

  const [selectedFile, setSelectedFile] = useState<PdfViewerFile | undefined>(
    undefined,
  );

  const [loadingFileId, setLoadingFileId] = useState<
    string | number | undefined
  >(undefined);

  const createFileUrl = useCreateFileUrl({
    externalId,
    onComplete: () => setLoadingFileId(undefined),
    setProgress: setCreateFileUrlProgress,
  });

  if (selectedFile) {
    return (
      <div className="flex h-[calc(95vh-140px)] w-full flex-col items-center gap-4 overflow-y-auto p-4">
        <div className="flex w-full items-center gap-4">
          <Button
            aria-label={translations.actions.back}
            color="primary"
            id="back"
            isIconOnly
            startContent={<ArrowLeftIcon />}
            variant="ghost"
            onPress={() => setSelectedFile(undefined)}
          />
          <div className="flex flex-col">
            <h3 className="text-heading-sm font-bold">
              {selectedFile.fileName}
            </h3>
            <p className="text-body-md text-gray-600">
              {translations.extractDocs.splitView.sourcesTab.uploadedFileDateDetail(
                {
                  date: formatUnixDate(selectedFile.uploadedAt, locale),
                },
              )}
            </p>
          </div>
        </div>
        <div className="w-full shadow-lg">
          <PdfViewer selectedFile={selectedFile} />
        </div>
      </div>
    );
  }

  if (isLoadingFiles) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner color="primary" size="lg" testId="spinner-loading-sources" />
      </div>
    );
  }

  if (!files?.length) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4">
        <Image
          alt={translations.extractDocs.splitView.sourcesTab.altTextEmptyState}
          className="h-[160px] w-auto overflow-visible"
          src={getStaticAsset("docs-empty-state.svg")}
        />
        <p className="w-[50%] text-center text-heading-md text-gray-600">
          {translations.extractDocs.splitView.sourcesTab.emptyStateInformation}
        </p>
      </div>
    );
  }

  return (
    <ul className="flex w-full flex-col gap-4 p-4">
      {files.map(({ file, name }) => {
        const isFileLoading =
          isLoading(createFileUrlProgress) && loadingFileId === file.id;

        return (
          <li key={`${name}-${file.updated_at}`} className="w-full">
            <Card
              aria-label={translations.extractDocs.splitView.sourcesTab.ariaLabelFileCard(
                { fileName: name },
              )}
              as="button"
              className="flex w-full flex-row rounded-lg border border-gray-300 bg-white"
              id={`source-card-${file.id}`}
              isLoading={isFileLoading}
              shadow="none"
              onPress={async () => {
                setLoadingFileId(file.id);

                const url = await createFileUrl(file);
                if (url) {
                  setSelectedFile({
                    fileName: name,
                    id: file.id,
                    uploadedAt: file.updated_at!,
                    url,
                  });
                }
              }}
            >
              <CardBody className="flex w-full flex-row items-center justify-start gap-4 px-6 py-4">
                {isFileLoading ? (
                  <Spinner
                    color="primary"
                    size="sm"
                    testId="spinner-loading-sources"
                  />
                ) : (
                  <FileTextIcon className="h-6 w-6 text-primary" />
                )}
                <div className="flex flex-col gap-1 text-left">
                  <span className="text-body-md font-bold text-gray-900">
                    {name}
                  </span>
                  <span className="text-body-sm text-gray-500">
                    {formatUnixDate(file.updated_at!, locale)}
                  </span>
                </div>
              </CardBody>
            </Card>
          </li>
        );
      })}
    </ul>
  );
};

const useFetchFiles = ({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) => {
  const [filesProgress, setFilesProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [files, setFiles] = useState<{ name: string; file: FileState }[]>([]);
  const toast = useToast();
  const translations = useTranslations();

  const params: FileParams = activateDTAAttachments
    ? `auction_id=${auction.id}&scope=${FILE_SCOPE.PATIENT_FILES}`
    : `auction_id=${auction.id}`;

  const { getFilesHISProgress, queryProgress, uploadedFilesV2 } =
    useGetFilesFeatures({
      action: undefined,
      externalId: patient.external_id,
      params,
    });

  useEffect(() => {
    (async () => {
      try {
        setFilesProgress(QUERY_PROGRESS_PENDING);
        const filesData = await Promise.all(
          uploadedFilesV2?.map(async (file) => {
            const props = await getFileNameAndIsNewVersionAvailable({
              canAccessPatientData: true,
              file,
            });

            if (props.shouldBlur) {
              return null;
            }

            if (typeof props.value === "string" && file.updated_at) {
              return { file, name: props.value };
            }

            return null;
          }) ?? [],
        );
        if (filesData.truthy()) {
          setFiles(filesData.truthy());
        }
        setFilesProgress(QUERY_PROGRESS_SUCCEED);
      } catch (error) {
        toast({
          color: "danger",
          message: translations.general.error,
        });
        console.error("Error fetching files:", error);
        setFilesProgress(QUERY_PROGRESS_FAILED);
      }
    })();
  }, [uploadedFilesV2]);

  return {
    files,
    isLoadingFiles: isLoading([
      filesProgress,
      getFilesHISProgress,
      queryProgress,
    ]),
  };
};

export const DocsModalSources = ({
  auction,
  patient,
}: {
  auction: Auction;
  patient: Patient;
}) => {
  const translations = useTranslations();
  const { files, isLoadingFiles } = useFetchFiles({
    auction,
    patient,
  });

  return (
    <div className="flex w-[50%] flex-col border-l border-gray-400">
      <div className="sticky top-0 w-full p-4">
        <h2 className="text-heading-lg font-bold">
          {translations.extractDocs.splitView.sourcesTab.title}
        </h2>
      </div>
      <hr className="w-full border-gray-400" />
      <div
        className={clsx("flex h-[calc(95vh-140px)] w-full overflow-y-auto", {
          "bg-gray-50": !files.length,
        })}
      >
        <SourceContent
          externalId={patient.external_id!}
          files={files}
          isLoadingFiles={isLoadingFiles}
        />
      </div>
    </div>
  );
};
